import type { SyntheticEvent } from 'react';
import { memo, useCallback } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Arrow, IconWrapper, ItemText, MenuBadge, TextBadgeGroup, StyledMenuItem } from './MenuItem.styles';
import type { IconNames } from '../Icon';
import { Icon } from '../Icon';

export const MenuItem = memo(
  ({
    active = false,
    open,
    dropdownOpen = false,
    text,
    icon,
    onClick,
    onArrowClick,
    onKeyDown,
    arrow = false,
    unreadNotificationsCount,
    ariaExpanded,
    ariaControls,
    ariaHasPopup,
    dataTestId,
    nested = false,
  }: {
    active?: boolean;
    dropdownOpen?: boolean;
    open: boolean;
    text: string;
    icon?: IconNames;
    onClick?: () => void;
    onArrowClick?: () => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    arrow?: boolean;
    unreadNotificationsCount?: number;
    ariaExpanded?: boolean;
    ariaControls?: string;
    ariaHasPopup?: 'true' | 'false' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
    dataTestId?: string;
    nested?: boolean;
  }) => {
    const handleArrowClick = useCallback(
      (e: SyntheticEvent) => {
        e.stopPropagation();
        onArrowClick?.();
      },
      [onArrowClick]
    );

    const handleClick = useCallback(
      (e: SyntheticEvent) => {
        (e.currentTarget as HTMLElement).blur();
        onClick?.();
      },
      [onClick]
    );

    const showBadge = unreadNotificationsCount !== undefined && unreadNotificationsCount > 0;

    const content = (
      <StyledMenuItem
        active={active.toString()}
        open={open}
        nested={nested.toString()}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-haspopup={ariaHasPopup}
        data-testid={dataTestId}>
        <div>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <IconWrapper>{icon && <Icon icon={icon} />}</IconWrapper>
            {!open && showBadge && (
              <MenuBadge
                data-testid={dataTestId ? `${dataTestId}-badge` : undefined}
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -8,
                  padding: '2px 4px',
                  minWidth: '20px',
                  height: '20px',
                }}>
                {unreadNotificationsCount}
              </MenuBadge>
            )}
          </Box>
          {open && (
            <TextBadgeGroup>
              <ItemText active={active}>{text}</ItemText>
              {showBadge && <MenuBadge data-testid={dataTestId ? `${dataTestId}-badge` : undefined}>{unreadNotificationsCount}</MenuBadge>}
            </TextBadgeGroup>
          )}
        </div>
        {open && arrow && <Arrow active={dropdownOpen.toString()} onClick={handleArrowClick} />}
      </StyledMenuItem>
    );

    if (!open) {
      return (
        <Tooltip title={text} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }

    return content;
  }
);
